/**
 * First match wins (D158) — the walk every conditional declaration makes.
 *
 * @remarks
 * The pack has one rule for choosing between authored readings: variants are
 * ordered, the first whose condition holds is the answer, and a `fallback`
 * carries the case where none does. Five modules each wrote that walk out by
 * hand — `resolveConditionalText`, `resolveRoomAmbience`, `resolveRoomImage`,
 * `resolvePacing` (whose comment already called itself "the fifth
 * restatement"), and the sprite roster — and D158 needed a sixth for the
 * exit lock's refusal.
 *
 * A sixth copy is where a rule stops being a rule, so it is stated here
 * instead. The declarations differ in the KEY the condition sits under
 * (`condition` in four of them, `when` in the D138 shapes) and in what they
 * hold, so the accessor is the caller's — everything else is shared.
 *
 * Every first-match walk reads it now (D159): the describer, the room's
 * plate, its bed, its pace, and the lock's refusal. The sprite roster is
 * NOT one of them — `visibleWhen` keeps every sprite whose condition holds,
 * which is a filter, not a first match — so the count is five, and five
 * is all of them.
 *
 * @packageDocumentation
 */

import type { ConditionDefinition, RuntimeState } from '@orpheus/content-schema';

import type { EngineGameBundle } from '../bundle/engine-game-bundle.js';
import type { CommandInHand } from './command-in-hand.js';

import { evaluateCondition } from './evaluate-condition.js';

/**
 * The first variant whose condition holds against the live world.
 *
 * @param variants - The authored variants, in author order
 * @param conditionOf - Where the condition sits on this variant shape
 * @param bundle - The loaded bundle
 * @param state - The live runtime state
 * @param commandInHand - D219: the command being answered, when there is one
 * @returns The winning variant, or `undefined` when none holds
 */
export const firstMatchingVariant = <Variant>(
	variants: readonly Variant[],
	conditionOf: (variant: Variant) => ConditionDefinition,
	bundle: EngineGameBundle,
	state: RuntimeState,
	commandInHand?: CommandInHand,
): Variant | undefined => variants.find((variant) => evaluateCondition(conditionOf(variant), bundle, state, commandInHand));
